import _clamp from 'lodash/clamp';
import _merge from 'lodash/merge';
import _debounce from 'lodash/debounce';
import _throttle from 'lodash/throttle';
import { Lightning } from '@lightningjs/sdk';
import { getTimeFormat } from '@bedrock-rdk/utils';
import { featureFlipping } from '@bedrock-rdk/config';
import { SEEK_DEBOUNCE_MS, SEEK_THROTTLE_MS, SEEK_STEP_SECONDS } from '../../player.constants';
import { safeProgressValue } from '../../../tornado/progress/progress.utils';
import { middle } from '../../../tornado/utils/sizes';
import { SliderThumb } from '../sliderThumb/sliderThumb.component';
import { DIAMETER } from '../sliderThumb/sliderThumb.style';
import Storyboard from './storyboard/storyboard.component';
import { STORYBOARD_WIDTH } from './storyboard/storyboard.style';
import { FRAMES_COUNT, FRAME_W } from './storyboard/storyboard.constants';
import { safeStoryboardValue } from './storyboard/storyboard.utils';
import { SeekBar } from './seekBar/seekBar.component';
import {
  defaultContext,
  getInitialStyle,
  getFocusedStyle,
  getUnFocusedStyle,
  LABEL_OFFSET,
  SLIDER_THUMB_TRANSITION,
  HEIGHT,
  getTimeLabelsFont,
  BAR_HEIGHT_UNFOCUSED,
  makeAdMarkerStyle,
} from './progressBar.style';
import { PAUSE_SIGNAL, SEEK_SIGNAL, PAUSE_ON_LONG_SEEK, TIME_ASIDE, TIME_HIDDEN } from './progressBar.constants';
import connect from './progressBar.connector';

export class ProgressBar extends Lightning.Component {
  static _template() {
    // TODO: move all the constants into the progressBar.style.js file
    return _merge(
      {
        Elapsed: {},
        Bar: {
          type: SeekBar,
        },
        Ads: {},
        SliderThumb: {
          type: SliderThumb,
        },
        Duration: {},
        Storyboard: {
          type: Storyboard,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._duration = 0;
    this._elapsed = 0;
    this._progress = 0;
    this._seekEnable = true;
    this._seekDirection = 0;
    this._playHead = 0;
    this._seekTime = 0;
    this._barWidth = 0;
    this._barOffset = 0;
    this._style = defaultContext;
    this._isLive = false;
    this._isActive = false;
    // Indicates if all the text labels widths are detected and the bar is placed accurately in a middle
    this._isBuiltCompletely = false;
    this._withStoryboard = false;
    this._variant = TIME_ASIDE;
    this._longSeekEnabled = false;
    this._ticks = 0;

    this._debounceDigitSeek = _debounce(seekTime => {
      this.signal(SEEK_SIGNAL, seekTime);
    }, SEEK_DEBOUNCE_MS);

    this._debounceSeek = _debounce(() => {
      this._seekDirection = 0;
      this._elapsed = this._seekTime;
      this.signal(SEEK_SIGNAL, this._seekTime);
    }, SEEK_DEBOUNCE_MS);

    this._throttleSeek = _throttle(() => {
      let step = SEEK_STEP_SECONDS;

      if (this._longSeekEnabled) {
        if (this._ticks > 20) {
          step = 120;
        } else if (this._ticks > 15) {
          step = 60;
        } else if (this._ticks > 10) {
          step = 30;
        }

        if (this._ticks > 1 && PAUSE_ON_LONG_SEEK) {
          this.signal(PAUSE_SIGNAL);
        }
      }

      this._seekTime = _clamp(this._seekTime + step * this._seekDirection, 0, this._duration);
      if (this._isLive && this._playHead) {
        this._seekTime = this._seekTime > this._playHead ? this._playHead : this._seekTime;
      }
      // Don't play seek animation if children sizes are not determined yet
      if (this._isBuiltCompletely) {
        this._setSeekTime(this._seekTime);
      }

      this._ticks += 1;

      this._debounceSeek();
    }, SEEK_THROTTLE_MS);
  }

  _build() {
    this.elapsed = 0;
    this._playHead = 0;
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _active() {
    this._isActive = true;
    this._handleProgressUpdate();
  }

  _inactive() {
    this._isActive = false;
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this.tag('Duration').removeAllListeners('txLoaded');
    this._isBuiltCompletely = false;
    this._stopSeeking();
  }

  get seekEnable() {
    return this._seekEnable;
  }

  set seekEnable(value) {
    this._seekEnable = value;
    this._handleProgressUpdate();
  }

  get longSeekEnabled() {
    return this._longSeekEnabled;
  }

  set longSeekEnabled(value) {
    this._longSeekEnabled = value;
  }

  get canSeek() {
    return this._seekEnable && this._duration > 0;
  }

  get withStoryboard() {
    return this._withStoryboard && !this._isLive;
  }

  set elapsed(value) {
    this._elapsed = value;
    this._updateTime();
  }

  set playHead(value) {
    this._playHead = value;
    this.tag('Bar').playHead = safeProgressValue(this._duration > 0 ? value / this._duration : 0);
  }

  set duration(value) {
    // Place dynamic controls on start position
    const duration = this.tag('Duration');

    if (this._duration !== value) {
      duration.text.text = getTimeFormat(value).replace(/\d/g, '0');
      // NOTE: force text texture to be loaded synchronously in order to measure as wide text as possible
      duration.loadTexture();
      const w = duration.finalW;
      this._barOffset = this._variant === TIME_ASIDE ? w + LABEL_OFFSET[this._style.screenSize] : 0;
    }

    this._duration = value;

    this._handleProgressUpdate();
    // Play seek animation if it was requested before Duration label texture was loaded
    this._isBuiltCompletely = true;
    if (this._seekDirection !== 0) {
      this._setSeekTime(this._seekTime);
    }
    // rolling back true duration value
    duration.text.text = getTimeFormat(value);

    this._placeAdMarkers();
  }

  set storyboard(value) {
    this._withStoryboard = value;
  }

  set storyboardId(id) {
    if (this.withStoryboard) {
      this.tag('Storyboard').imageId = id;
    }
  }

  set isLive(value) {
    this._isLive = value;
    this.tag('Bar').isLive = value;
  }

  get w() {
    return super.w;
  }

  set w(value) {
    super.w = value;
    this._handleProgressUpdate();
  }

  get variant() {
    return this._variant;
  }

  set variant(value) {
    this._variant = value;
    const showProgress = this._variant !== TIME_HIDDEN;
    this.patch({
      Elapsed: {
        visible: showProgress,
      },
      Duration: {
        visible: showProgress,
      },
    });
    this._handleProgressUpdate();
  }

  set adMarkers(value) {
    this._adMarkers = value;
    this._placeAdMarkers();
  }

  _placeAdMarkers() {
    if (!this._adMarkers || !this._duration || !this._barWidth || !featureFlipping.player_midrollMarkers) {
      return;
    }

    this.tag('Ads').children = this._adMarkers
      .filter(cue => cue <= this._duration)
      .map(cue => {
        const offset = cue / this._duration;
        const x = Math.round(offset * this._barWidth);

        return {
          ...makeAdMarkerStyle(this._style),
          x,
        };
      });
  }

  _focus() {
    this.patch(getFocusedStyle(this._style.screenSize));
  }

  _unfocus() {
    if (this._seekDirection !== 0) {
      this._seekDirection = 0;
      this._elapsed = this._seekTime;
      this.signal(SEEK_SIGNAL, this._seekTime);
    }
    this._stopSeeking();
    this.patch(getUnFocusedStyle(this._style.screenSize));
  }

  _handleKeyRelease() {
    this._ticks = 0;
  }

  _handleLeft() {
    return this._handleRewind();
  }

  _handleRight() {
    return this._handleFastForward();
  }

  _handleSkipFastForward() {
    return this._handleFastForward();
  }

  _handleSkipRewind() {
    return this._handleRewind();
  }

  _handleRewind() {
    return this._startSeeking(-1);
  }

  _handleFastForward() {
    return this._startSeeking(1);
  }

  // NOTE: this one should be kept as Metrological app store may use both FastForward and Forward for key definition
  _handleForward() {
    return this._handleFastForward();
  }

  _handleKey(event) {
    if (this._seekDirection === 0 && this._duration && /^[0-9]$/.test(event.key)) {
      const seekTime = Math.round(this._duration * (parseInt(event.key, 10) / 10));

      this._debounceDigitSeek(seekTime);

      return true;
    }

    return false;
  }

  _startSeeking(direction) {
    if (!this.canSeek) {
      return false;
    }

    this._updateTime();
    this._seekDirection = direction;
    this._throttleSeek();

    return true;
  }

  _stopSeeking() {
    this._ticks = 0;
    this._seekDirection = 0;
    this._debounceSeek.cancel();
    this._throttleSeek.cancel();
    this._debounceDigitSeek.cancel();
  }

  _handleProgressUpdate() {
    if (!this._isActive) {
      return;
    }
    this._barWidth = this.finalW - this._barOffset * 2;
    const { screenSize } = this._style;
    const isAside = this._variant === TIME_ASIDE;
    const h = isAside
      ? HEIGHT[screenSize]
      : DIAMETER[screenSize] + LABEL_OFFSET[screenSize] + getTimeLabelsFont(screenSize).lineHeight;
    const y = isAside ? Math.round(h / 2) : h;
    const mountY = isAside ? 0.5 : 1;
    const barY = isAside ? middle : Math.round((DIAMETER[screenSize] - BAR_HEIGHT_UNFOCUSED[screenSize]) / 2);

    this.patch({
      h,
      Elapsed: { y, mountY },
      Bar: {
        x: this._barOffset,
        w: this._barWidth,
        mountY: isAside ? 0.5 : 0,
        y: barY,
      },
      Ads: {
        x: this._barOffset,
        mountY: isAside ? 0.5 : 0,
        y: barY,
        w: this._barWidth,
      },
      SliderThumb: {
        visible: this.canSeek,
        mountY: isAside ? 0.5 : 0,
        y: isAside ? middle : 0,
      },
      Duration: {
        x: this.finalW,
        y,
        mountY,
        mountX: 1,
      },
    });

    this._placeAdMarkers();
    this._updateTime();
  }

  _updateTime() {
    this._progress =
      this._variant === TIME_HIDDEN ? 1 : safeProgressValue(this._duration > 0 ? this._elapsed / this._duration : 0);

    if (this._seekDirection === 0) {
      this._seekTime = this._elapsed;
      this.tag('Elapsed').text.text = getTimeFormat(this._elapsed);
      this.tag('Bar').progress = this._progress;
      this.tag('SliderThumb').x = this._getSliderPosition(this._progress);
    }
  }

  _getSliderPosition(progress) {
    return this._barOffset + progress * this._barWidth;
  }

  _setSeekTime(time, shouldAnimate = true) {
    const progress = Math.min(time / this._duration, 1);
    const sliderThumbPosition = this._getSliderPosition(progress);
    this._updateStoryboard(progress, shouldAnimate);

    if (shouldAnimate) {
      this.tag('SliderThumb').setSmooth('x', sliderThumbPosition, SLIDER_THUMB_TRANSITION);
      this.tag('Bar').setSmooth('progress', progress, SLIDER_THUMB_TRANSITION);
    } else {
      this.tag('SliderThumb').x = sliderThumbPosition;
      this.tag('Bar').progress = progress;
    }
    this.tag('Elapsed').text.text = getTimeFormat(time);
  }

  _updateStoryboard(progress, shouldAnimate) {
    if (this.withStoryboard) {
      const storyboard = this.tag('Storyboard');
      const storyboardPosition = this._getStoryboardPosition(progress);
      const framePosition = safeStoryboardValue(Math.max(1, Math.floor(FRAMES_COUNT * progress) * FRAME_W));

      storyboard.updateView(framePosition);
      if (shouldAnimate && storyboard.isVisible) {
        storyboard.setSmooth('x', storyboardPosition, SLIDER_THUMB_TRANSITION);

        return;
      }

      storyboard.x = storyboardPosition;
    }
  }

  _getStoryboardPosition(progress) {
    const sliderThumbPosition = this._getSliderPosition(progress);
    const storyboardCenter = Math.round(STORYBOARD_WIDTH / 2);
    const endPoint = this._barWidth + this._barOffset;

    if (endPoint < sliderThumbPosition + storyboardCenter) {
      return endPoint - STORYBOARD_WIDTH;
    }
    if (sliderThumbPosition > storyboardCenter + this._barOffset) {
      return sliderThumbPosition - storyboardCenter;
    }

    return this._barOffset;
  }
}
